<template>
  <div class="mt-3">
    <div class="hc-card mb-3">
      <div class="card-body p-3">
        <h6 class="mb-0 font-weight-bold">Product Library Builder</h6>
        <small class="text-muted">Search the shared product catalog, build a customer's opening inventory, then post it straight into their company.</small>

        <!-- Search -->
        <div class="position-relative mt-3" v-click-outside="closeSearch">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text bg-white border-right-0">
                <i class="bi bi-search text-muted"></i>
              </span>
            </div>
            <input
              v-model="search.query"
              @input="onSearchInput"
              @focus="search.open = true"
              @keydown.esc="closeSearch"
              class="form-control border-left-0"
              placeholder="Search products, e.g. Panadol…"
              autocomplete="off"
            />
          </div>

          <div
            v-if="search.open && (search.results.length > 0 || search.loading || search.query.trim())"
            class="position-absolute bg-white border w-100"
            style="top:100%;left:0;z-index:1060;max-height:320px;overflow-y:auto;border-radius:12px;border-color:#e9ecef !important;"
          >
            <div v-if="search.loading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-secondary"></div>
            </div>
            <template v-else>
              <a
                v-for="item in search.results"
                :key="item.id"
                class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                style="cursor:pointer;text-decoration:none;color:inherit;"
                @click="addToCart(item)"
              >
                <div>
                  <div class="font-weight-bold" style="font-size:.85rem;">{{ item.name }}</div>
                  <small class="text-muted">{{ item.category || 'Uncategorized' }}</small>
                </div>
                <i class="bi bi-plus-circle text-primary"></i>
              </a>
              <div v-if="search.results.length === 0" class="text-center text-muted py-2">
                <small>No matches</small>
              </div>
              <a
                class="d-flex align-items-center px-3 py-2 bg-light"
                style="cursor:pointer;text-decoration:none;color:inherit;"
                @click="openCreateProductModal"
              >
                <i class="bi bi-plus-circle mr-2 text-primary"></i>
                <span class="font-weight-bold" style="font-size:.85rem;">Create New Product{{ search.query.trim() ? `: "${search.query.trim()}"` : '' }}</span>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Cart -->
    <div class="hc-card">
      <div class="card-body p-3">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <h6 class="mb-2 mb-md-0 font-weight-bold">Onboarding List ({{ cart.length }})</h6>
          <div class="d-flex">
            <button class="btn btn-outline-secondary mr-2 flex-fill flex-md-grow-0" :disabled="cart.length === 0 || exporting" @click="downloadExcel">
              <span v-if="exporting" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-download mr-1"></i> Download as Excel
            </button>
            <button class="btn btn-primary flex-fill flex-md-grow-0" :disabled="cart.length === 0" @click="openCompanySelector">
              <i class="bi bi-send mr-1"></i> Post to Company
            </button>
          </div>
        </div>

        <div v-if="cart.length === 0" class="text-center text-muted py-4">
          <small>Search above to add products to this onboarding list.</small>
        </div>

        <template v-else>
          <!-- Desktop / tablet: full table -->
          <div class="table-responsive d-none d-md-block">
            <table class="table table-bordered mb-0">
              <thead class="thead-light">
                <tr>
                  <th style="min-width:160px;">Product</th>
                  <th style="width:130px;">Category <span class="text-danger">*</span></th>
                  <th style="width:120px;">Measuring Unit <span class="text-danger">*</span></th>
                  <th style="width:110px;">Remaining Qty</th>
                  <th style="width:110px;">Buying Price</th>
                  <th style="width:110px;">Selling Price</th>
                  <th style="width:130px;">Expiry Date</th>
                  <th style="width:90px;">Is Service</th>
                  <th style="width:110px;">Batch No.</th>
                  <th style="width:110px;">Barcode</th>
                  <th style="width:110px;">Location</th>
                  <th style="width:40px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in cart" :key="index">
                  <td class="align-middle font-weight-bold" style="font-size:.85rem;">{{ row.name }}</td>
                  <td>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !row.category.trim() }"
                      v-model="row.category"
                      :placeholder="row.category_suggestion || 'Required'"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !row.measuring_unit.trim() }"
                      v-model="row.measuring_unit"
                      :placeholder="row.measuring_unit_suggestion || 'Required'"
                    />
                  </td>
                  <td><input type="number" min="0" class="form-control" v-model.number="row.opening_qty" /></td>
                  <td><input type="number" min="0" class="form-control" v-model.number="row.buying_price" /></td>
                  <td><input type="number" min="0" class="form-control" v-model.number="row.selling_price" /></td>
                  <td>
                    <date-range-picker
                      :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true"
                      :date-range="{ startDate: row.expiry_date ? new Date(row.expiry_date) : null }"
                      @update="val => row.expiry_date = val && val.startDate ? formatPickerValue(val.startDate) : ''"
                      class="custom-width-datepicker"
                    >
                      <template #input="picker">
                        <span v-if="picker.startDate">{{ formatPickerDate(picker.startDate) }}</span>
                        <span v-else class="text-muted"><i class="bi bi-calendar-event"></i></span>
                      </template>
                    </date-range-picker>
                  </td>
                  <td class="align-middle text-center"><input type="checkbox" v-model="row.is_service" /></td>
                  <td><input type="text" class="form-control" v-model="row.batch_number" /></td>
                  <td><input type="text" class="form-control" v-model="row.barcode" /></td>
                  <td><input type="text" class="form-control" v-model="row.location" /></td>
                  <td class="align-middle text-center">
                    <button class="btn btn-outline-danger" @click="cart.splice(index, 1)">
                      <i class="bi bi-x"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile: one card per product, required fields up front, rest tucked behind "More details" -->
          <div class="d-md-none">
            <div class="hc-card mb-2" v-for="(row, index) in cart" :key="'m-' + index">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="mb-0 font-weight-bold">{{ row.name }}</h6>
                  <button class="btn btn-outline-danger" @click="cart.splice(index, 1)">
                    <i class="bi bi-x"></i>
                  </button>
                </div>

                <div class="row no-gutters">
                  <div class="col-6 pr-1 mb-2">
                    <label class="font-weight-bold mb-1">Category <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !row.category.trim() }"
                      v-model="row.category"
                      :placeholder="row.category_suggestion || 'Required'"
                    />
                  </div>
                  <div class="col-6 pl-1 mb-2">
                    <label class="font-weight-bold mb-1">Measuring Unit <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !row.measuring_unit.trim() }"
                      v-model="row.measuring_unit"
                      :placeholder="row.measuring_unit_suggestion || 'Required'"
                    />
                  </div>
                  <div class="col-4 pr-1 mb-2">
                    <label class="font-weight-bold mb-1">Qty</label>
                    <input type="number" min="0" class="form-control" v-model.number="row.opening_qty" />
                  </div>
                  <div class="col-4 px-1 mb-2">
                    <label class="font-weight-bold mb-1">Buying</label>
                    <input type="number" min="0" class="form-control" v-model.number="row.buying_price" />
                  </div>
                  <div class="col-4 pl-1 mb-2">
                    <label class="font-weight-bold mb-1">Selling</label>
                    <input type="number" min="0" class="form-control" v-model.number="row.selling_price" />
                  </div>
                </div>

                <button class="btn btn-link p-0 text-secondary" @click="row.showMore = !row.showMore">
                  <i :class="['bi', row.showMore ? 'bi-chevron-up' : 'bi-chevron-down', 'mr-1']"></i>
                  {{ row.showMore ? 'Hide' : 'More' }} details
                </button>

                <div class="row no-gutters mt-2" v-show="row.showMore">
                  <div class="col-6 pr-1 mb-2">
                    <label class="font-weight-bold mb-1">Expiry Date</label>
                    <date-range-picker
                      :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true"
                      :date-range="{ startDate: row.expiry_date ? new Date(row.expiry_date) : null }"
                      @update="val => row.expiry_date = val && val.startDate ? formatPickerValue(val.startDate) : ''"
                      class="custom-width-datepicker"
                    >
                      <template #input="picker">
                        <span v-if="picker.startDate">{{ formatPickerDate(picker.startDate) }}</span>
                        <span v-else class="text-muted"><i class="bi bi-calendar-event"></i> Select date</span>
                      </template>
                    </date-range-picker>
                  </div>
                  <div class="col-6 pl-1 mb-2 d-flex align-items-end">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" :id="'mobile-svc-' + index" v-model="row.is_service" />
                      <label class="form-check-label" :for="'mobile-svc-' + index">Is Service</label>
                    </div>
                  </div>
                  <div class="col-6 pr-1 mb-2">
                    <label class="font-weight-bold mb-1">Batch No.</label>
                    <input type="text" class="form-control" v-model="row.batch_number" />
                  </div>
                  <div class="col-6 pl-1 mb-2">
                    <label class="font-weight-bold mb-1">Barcode</label>
                    <input type="text" class="form-control" v-model="row.barcode" />
                  </div>
                  <div class="col-12 mb-2">
                    <label class="font-weight-bold mb-1">Location</label>
                    <input type="text" class="form-control" v-model="row.location" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Create New Product modal -->
    <b-modal ref="createProductModal" title="Create New Product" hide-footer>
      <form @submit.prevent="submitCreateProduct">
        <div class="form-group">
          <label class="font-weight-bold">Product Name</label>
          <input v-model="newProduct.name" class="form-control" required />
        </div>
        <div class="form-group">
          <label class="font-weight-bold">Category</label>
          <input v-model="newProduct.category" class="form-control" placeholder="Optional" />
        </div>
        <div class="form-group">
          <label class="font-weight-bold">Measuring Unit</label>
          <input v-model="newProduct.measuring_unit" class="form-control" placeholder="Optional, e.g. Piece, Box" />
        </div>
        <button class="btn btn-primary btn-block mt-3" type="submit" :disabled="newProduct.saving">
          <span v-if="newProduct.saving" class="spinner-border spinner-border-sm mr-1"></span>
          Create &amp; Add to List
        </button>
      </form>
    </b-modal>

    <!-- Company selector modal -->
    <b-modal ref="companyModal" title="Post to Company" hide-footer size="lg">
      <div class="position-relative" v-click-outside="closeCompanySearch">
        <input
          v-model="companySearch.query"
          @input="onCompanySearchInput"
          class="form-control"
          placeholder="Search company by name, email or phone…"
          autocomplete="off"
        />
        <div
          v-if="companySearch.results.length > 0 || companySearch.loading"
          class="border rounded mt-1"
          style="max-height:260px;overflow-y:auto;"
        >
          <div v-if="companySearch.loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-secondary"></div>
          </div>
          <a
            v-else
            v-for="company in companySearch.results"
            :key="company.id"
            class="d-block px-3 py-2 border-bottom"
            style="cursor:pointer;text-decoration:none;color:inherit;"
            :class="{ 'bg-light': selectedCompany && selectedCompany.id === company.id }"
            @click="selectedCompany = company"
          >
            <div class="font-weight-bold" style="font-size:.85rem;">{{ company.name }}</div>
            <small class="text-muted">{{ company.email }}</small>
          </a>
        </div>
      </div>

      <div v-if="selectedCompany" class="mt-3 p-2 border rounded d-flex justify-content-between align-items-center">
        <div>
          <small class="text-muted d-block">Posting {{ cart.length }} product(s) to</small>
          <span class="font-weight-bold">{{ selectedCompany.name }}</span>
        </div>
        <button class="btn btn-outline-secondary" @click="selectedCompany = null">Change</button>
      </div>

      <button
        class="btn btn-primary btn-block mt-3"
        :disabled="!selectedCompany || posting"
        @click="confirmPostToCompany"
      >
        <span v-if="posting" class="spinner-border spinner-border-sm mr-1"></span>
        Confirm &amp; Import
      </button>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'ProductLibraryBuilder',

  components: { DateRangePicker },

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  data() {
    return {
      search: {
        query: '',
        results: [],
        loading: false,
        open: false,
        debounceTimer: null,
      },
      cart: [],
      newProduct: {
        name: '',
        category: '',
        measuring_unit: '',
        saving: false,
      },
      exporting: false,
      companySearch: {
        query: '',
        results: [],
        loading: false,
        debounceTimer: null,
      },
      selectedCompany: null,
      posting: false,
    };
  },

  created() {
    try {
      const saved = localStorage.getItem('productLibraryCart');
      if (saved) this.cart = JSON.parse(saved);
    } catch (e) {}
  },

  watch: {
    cart: {
      deep: true,
      handler(v) {
        try { localStorage.setItem('productLibraryCart', JSON.stringify(v)); } catch (e) {}
      },
    },
  },

  methods: {
    formatPickerValue(d) {
      return moment(d).format('YYYY-MM-DD');
    },
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    onSearchInput() {
      clearTimeout(this.search.debounceTimer);
      this.search.open = true;

      if (!this.search.query.trim()) {
        this.search.results = [];
        return;
      }

      this.search.loading = true;
      this.search.debounceTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/admin/product-library/search', {
            params: { q: this.search.query.trim() },
          });
          this.search.results = data.items;
        } finally {
          this.search.loading = false;
        }
      }, 250);
    },

    closeSearch() {
      this.search.open = false;
    },

    addToCart(item) {
      // Category/measuring unit from the shared library (or just typed into the
      // Create New Product modal) are a starting suggestion only — different
      // companies categorize the same product differently, so the officer must
      // confirm or override them per customer rather than have them silently copied.
      this.cart.push({
        library_item_id: item.id,
        name: item.name,
        category: '',
        category_suggestion: item.category || '',
        measuring_unit: '',
        measuring_unit_suggestion: item.measuring_unit || '',
        opening_qty: null,
        buying_price: null,
        selling_price: null,
        expiry_date: null,
        is_service: false,
        batch_number: '',
        barcode: '',
        location: '',
        showMore: false,
      });
      this.search.query = '';
      this.search.results = [];
      this.search.open = false;
    },

    openCreateProductModal() {
      this.newProduct = { name: this.search.query.trim(), category: '', measuring_unit: '', saving: false };
      this.search.open = false;
      this.$refs.createProductModal.show();
    },

    async submitCreateProduct() {
      this.newProduct.saving = true;
      try {
        const { data } = await axios.post('/api/admin/product-library/products', {
          name: this.newProduct.name,
          category: this.newProduct.category || null,
          measuring_unit: this.newProduct.measuring_unit || null,
        });
        this.addToCart(data.item);
        this.$refs.createProductModal.hide();
        this.$toasts.push({ message: 'Product added to library', type: 'success' });
      } catch (e) {
        this.$toasts.push({
          message: e.response?.data?.message || 'Failed to create product',
          type: 'error',
        });
      } finally {
        this.newProduct.saving = false;
      }
    },

    // A row without its own typed value falls back to the library's
    // suggestion (shown as the input's placeholder) instead of blocking.
    resolvedCartItems() {
      return this.cart.map(row => ({
        ...row,
        category: row.category.trim() || row.category_suggestion.trim(),
        measuring_unit: row.measuring_unit.trim() || row.measuring_unit_suggestion.trim(),
      }));
    },

    validateCart() {
      const missing = this.resolvedCartItems().filter(row => !row.category.trim() || !row.measuring_unit.trim());
      if (missing.length > 0) {
        this.$toasts.push({
          message: `Category and Measuring Unit are required — missing for: ${missing.map(r => r.name).join(', ')}`,
          type: 'error',
        });
        return false;
      }
      return true;
    },

    async downloadExcel() {
      if (!this.validateCart()) return;

      this.exporting = true;
      try {
        const response = await axios.post(
          '/api/admin/product-library/export',
          { items: this.resolvedCartItems() },
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'product-library-cart.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        this.$toasts.push({ message: 'Failed to generate Excel file', type: 'error' });
      } finally {
        this.exporting = false;
      }
    },

    openCompanySelector() {
      this.companySearch = { query: '', results: [], loading: false, debounceTimer: null };
      this.selectedCompany = null;
      this.$refs.companyModal.show();
    },

    onCompanySearchInput() {
      clearTimeout(this.companySearch.debounceTimer);

      if (!this.companySearch.query.trim()) {
        this.companySearch.results = [];
        return;
      }

      this.companySearch.loading = true;
      this.companySearch.debounceTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/admin/companies', {
            params: { search: this.companySearch.query.trim(), per_page: 8 },
          });
          this.companySearch.results = data.data;
        } finally {
          this.companySearch.loading = false;
        }
      }, 300);
    },

    closeCompanySearch() {
      this.companySearch.results = [];
    },

    async confirmPostToCompany() {
      if (!this.selectedCompany) return;
      if (!this.validateCart()) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Import ${this.cart.length} product(s) into ${this.selectedCompany.name}? This creates real inventory for that company.`,
        { title: 'Confirm Import', okVariant: 'primary', okTitle: 'Import', centered: true }
      );
      if (!confirmed) return;

      this.posting = true;
      try {
        const { data } = await axios.post('/api/admin/product-library/post-to-company', {
          company_id: this.selectedCompany.id,
          items: this.resolvedCartItems(),
        });

        if (data.errors && data.errors.length > 0) {
          this.$toasts.push({
            message: `Imported ${data.created_count} product(s), ${data.errors.length} row(s) had errors.`,
            type: 'error',
          });
        } else {
          this.$toasts.push({ message: `Imported ${data.created_count} product(s) successfully`, type: 'success' });
          this.cart = [];
        }

        this.$refs.companyModal.hide();
      } catch (e) {
        this.$toasts.push({
          message: e.response?.data?.message || 'Failed to import products',
          type: 'error',
        });
      } finally {
        this.posting = false;
      }
    },
  },
};
</script>

<style scoped>
.hc-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
</style>
